import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Fonction pour formater les dates
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString('fr-CA');
};

// Fonction pour formater les montants
const formatMontant = (montant, monnaie = 'MAD') => {
  if (montant === null || montant === undefined) return '0.00';
  const formatted = parseFloat(montant).toFixed(2);
  return monnaie === 'CAD' 
    ? `${formatted} $ CAD`
    : `${formatted} DH`;
};

// Construction du document PDF d'avoir
async function buildAvoirPdfDoc(avoir, settings = {}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  
  // Charger le logo - utiliser logoBase64 si fourni, sinon charger depuis URL
  let logoDataUrl = settings.logoBase64 || null;
  
  if (!logoDataUrl && settings.logo) {
    try {
      let logoUrl = settings.logo;
      if (!logoUrl.startsWith('data:') && !logoUrl.startsWith('http')) {
        logoUrl = `${process.env.REACT_APP_API_URL}${logoUrl}`;
      }
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      logoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Erreur lors du chargement du logo:', err);
    }
  }
  
  // 🔴 HEADER - LOGO + INFO ENTREPRISE
  let yPosition = margin;
  
  // Logo à gauche - plus grand et mieux positionné
  if (logoDataUrl) {
    try {
      // Utiliser la même taille que dans les factures pour cohérence
      doc.addImage(logoDataUrl, 'PNG', 10, 8, 70, 55);
    } catch (err) {
      console.error('Erreur lors de l\'ajout du logo:', err);
      // Fallback: logo de remplacement IMMIGREAT
      doc.setFillColor(211, 47, 47); // Rouge IMMIGREAT
      doc.rect(10, 8, 70, 55, 'F');
      doc.setFillColor(255, 255, 255);
      doc.rect(12, 10, 66, 51, 'F');
      doc.setLineWidth(2);
      doc.setDrawColor(211, 47, 47);
      doc.rect(10, 8, 70, 55, 'S');
      doc.setFontSize(12);
      doc.setTextColor(211, 47, 47);
      doc.text('IMMIGREAT', 25, 35);
      doc.setFontSize(8);
      doc.text('SERVICES D\'IMMIGRATION', 18, 45);
    }
  }
  
  // Info entreprise à droite (aligné avec le haut du logo)
  let infoYPosition = 15;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  const entrepriseNom = settings.nom || 'Services d\'Immigration Expert';
  doc.text(entrepriseNom.toUpperCase(), pageWidth - margin, infoYPosition, { align: 'right' });
  
  infoYPosition += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  
  // Adresse complète formatée
  const adresseComplete = settings.adresse || 'ESPACE LES CHAMPS 1-46, 1 LOT PRESTIGIA, CHAMPS DE COURSES, Fès, Maroc';
  const adresseParts = adresseComplete.split(',').map(part => part.trim());
  
  adresseParts.forEach(part => {
    doc.text(part, pageWidth - margin, infoYPosition, { align: 'right' });
    infoYPosition += 5;
  });
  
  if (settings.telephone) {
    doc.text(`Tél: ${settings.telephone}`, pageWidth - margin, infoYPosition, { align: 'right' });
    infoYPosition += 5;
  }
  if (settings.email) {
    doc.text(`Email: ${settings.email}`, pageWidth - margin, infoYPosition, { align: 'right' });
  }
  
  // 🔴 TITRE - AVOIR
  yPosition = 60;
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38); // Rouge
  doc.text('AVOIR', pageWidth / 2, yPosition, { align: 'center' });
  
  // Numéro d'avoir
  yPosition += 10;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(`N° ${avoir.numero_avoir}`, pageWidth / 2, yPosition, { align: 'center' });
  
  // 🔴 INFORMATIONS DE L'AVOIR
  yPosition += 15;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Date d\'émission:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(avoir.date_emission), margin + 45, yPosition);
  
  yPosition += 7;
  doc.setFont('helvetica', 'bold');
  doc.text('Facture concernée:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(avoir.facture_numero || 'N/A', margin + 45, yPosition);
  
  yPosition += 7;
  doc.setFont('helvetica', 'bold');
  doc.text('Statut:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  const statutText = avoir.statut === 'actif' ? 'Actif' : avoir.statut === 'utilise' ? 'Utilisé' : 'Expiré';
  doc.text(statutText, margin + 45, yPosition);
  
  // 🔴 CLIENT
  yPosition += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT:', margin, yPosition);
  
  yPosition += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(avoir.client_nom || 'Client inconnu', margin, yPosition);
  
  if (avoir.client && avoir.client.email) {
    yPosition += 6;
    doc.text(`Email: ${avoir.client.email}`, margin, yPosition);
  }
  
  if (avoir.client && avoir.client.telephone) {
    yPosition += 6;
    doc.text(`Tél: ${avoir.client.telephone}`, margin, yPosition);
  }
  
  // 🔴 RAISON DE L'AVOIR
  yPosition += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('RAISON:', margin, yPosition);
  
  yPosition += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const raisonLines = doc.splitTextToSize(avoir.raison || 'Non spécifiée', pageWidth - 2 * margin);
  doc.text(raisonLines, margin, yPosition);
  yPosition += raisonLines.length * 5 + 5;
  
  // 🔴 TABLEAU DES PRESTATIONS (si disponible)
  if (avoir.prestations_details) {
    try {
      const prestations = JSON.parse(avoir.prestations_details);
      if (Array.isArray(prestations) && prestations.length > 0) {
        yPosition += 10;
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('DÉTAILS DES PRESTATIONS ANNULÉES:', margin, yPosition);
        
        yPosition += 8;
        
        const tableData = prestations.map(p => [
          p.description || '',
          p.quantite || 1,
          formatMontant(p.prix_unitaire || 0, avoir.monnaie),
          formatMontant((p.quantite || 1) * (p.prix_unitaire || 0), avoir.monnaie)
        ]);
        
        doc.autoTable({
          startY: yPosition,
          head: [['Description', 'Qté', 'Prix unitaire', 'Total']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [220, 38, 38],
            textColor: 255,
            fontSize: 10,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 9,
            cellPadding: 3
          },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 20, halign: 'center' },
            2: { cellWidth: 35, halign: 'right' },
            3: { cellWidth: 35, halign: 'right' }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
    } catch (err) {
      console.error('Erreur lors du parsing des prestations:', err);
    }
  }
  
  // 🔴 MONTANT TOTAL DE L'AVOIR
  yPosition += 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38);
  
  const montantText = `MONTANT DE L'AVOIR: ${formatMontant(avoir.montant, avoir.monnaie)}`;
  doc.text(montantText, pageWidth - margin, yPosition, { align: 'right' });
  
  // 🔴 NOTES SUPPLÉMENTAIRES
  if (avoir.notes) {
    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('NOTES:', margin, yPosition);
    
    yPosition += 6;
    doc.setFont('helvetica', 'normal');
    const notesLines = doc.splitTextToSize(avoir.notes, pageWidth - 2 * margin);
    doc.text(notesLines, margin, yPosition);
  }
  
  // 🔴 FOOTER
  const footerY = pageHeight - 20;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text(`${entrepriseNom} | Merci de votre confiance`, margin, footerY);
  doc.text(`Avoir généré le ${new Date().toLocaleDateString('fr-CA')}`, pageWidth - margin - 60, footerY);
  
  // Message important
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38);
  doc.text('Cet avoir peut être utilisé pour de futurs services.', pageWidth / 2, footerY + 5, { align: 'center' });
  
  return doc;
}

// Ouvre le PDF dans un nouvel onglet
export async function openAvoirPdfInNewTab(avoir, settings = {}) {
  const doc = await buildAvoirPdfDoc(avoir, settings);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

// Télécharge le PDF
export async function downloadAvoirPdf(avoir, settings = {}) {
  const doc = await buildAvoirPdfDoc(avoir, settings);
  const numero = avoir.numero_avoir || avoir.id || 'avoir';
  doc.save(`Avoir-${numero}.pdf`);
}

// Imprime le PDF
export async function printAvoirPdf(avoir, settings = {}) {
  const doc = await buildAvoirPdfDoc(avoir, settings);
  window.open(doc.output('bloburl'), '_blank');
}

export default {
  openAvoirPdfInNewTab,
  downloadAvoirPdf,
  printAvoirPdf
};
